import type { DropdownWidgetData } from 'blocks/shared/dropdown-widget/dropdown-widget.ts';
import type {
	FilterDefinition,
	NestedFilterValue,
} from 'blocks/shared/search-filters/search-filters.ts';
import {
	SearchFilters,
	type FilterValues,
} from 'blocks/shared/search-filters/search-filters.ts';
import { DEFAULT_LANG_CODE } from 'shared/constants.ts';
import type { Placeholders } from 'utils/fetchPlaceholders.ts';
import { getLanguageFromPath } from 'utils/languageUtils.ts';
import type { DownloadSortOptions } from './download-search-result.ts';
import type {
	Download,
	DownloadLanguage,
	DownloadProduct,
	DownloadProductLegacy,
	DownloadType,
} from './downloads.types.ts';

export type GroupedDownload = {
	Title?: string;
	Product: string;
	Products: string[];
	Version?: string;
	Type?: string;
	Date?: number;
	downloads: Array<Omit<Download, 'Language'> & { Language: string }>;
};

/**
 * Extracts the appropriate marketing content path for the current language from a comma-separated list.
 *
 * This function processes marketing content that may contain multiple language-specific paths
 * separated by commas. It attempts to find the path that matches the current page language,
 * and falls back to the default language if no match is found.
 *
 * @param marketingContent - Optional comma-separated string containing marketing content paths
 *                          for different languages (e.g., "/en/path1,/fr/path2,/es/path3")
 *
 * @returns The marketing content path for the current language, or the default language path
 *          if no match is found, or undefined if no marketing content is provided
 *
 * @example
 * // If current language is 'fr' and marketingContent is "/en/path1,/fr/path2,/es/path3"
 * // Returns: "/fr/path2"
 *
 * @example
 * // If current language is 'de' and marketingContent is "/en/path1,/fr/path2"
 * // Returns: "/en/path1" (falls back to default language)
 */
function getMarketingPathForLanguage(marketingContent?: string) {
	if (!marketingContent) return;

	const language = getLanguageFromPath() || DEFAULT_LANG_CODE;
	const marketingContentLanguages = marketingContent
		?.split(',')
		?.map((mc) => {
			const marketingContent = mc.trim();
			if (!marketingContent) return;

			const marketingContentLanguage =
				getLanguageFromPath(marketingContent) || DEFAULT_LANG_CODE;
			return {
				marketingContentLanguage,
				marketingContent,
			};
		})
		.filter((l) => l !== undefined);

	const marketingContentLanguage = marketingContentLanguages?.find(
		(l) => l.marketingContentLanguage === language,
	);

	if (marketingContentLanguage)
		return marketingContentLanguage.marketingContent;

	// Fallback to default language if no language is found
	return marketingContentLanguages?.find(
		(l) => l.marketingContentLanguage === DEFAULT_LANG_CODE,
	)?.marketingContent;
}

export function groupDownloadsByFields(
	downloads: Download[],
	placeholders: Placeholders,
): GroupedDownload[] {
	// Create a map to store the grouped downloads
	const groupedMap = new Map<string, GroupedDownload>();

	// Helper function to create a unique key for grouping
	const createGroupKey = (download: Download): string => {
		return `${download.Title || ''}-${download.Product}-${download.Version || ''}-${download.Type || ''}`.toLowerCase();
	};

	// Group the downloads
	downloads.forEach((download) => {
		const key = createGroupKey(download);

		if (!groupedMap.has(key)) {
			groupedMap.set(key, {
				Title: download.Title,
				Product: download.Product,
				Products:
					download.Product.split(',')?.map((d) => d.trim().toLowerCase()) ?? [],
				Version: download.Version,
				Type: download.Type,
				downloads: [],
			});
		}

		groupedMap.get(key)?.downloads.push({
			...download,
			MarketingContent: getMarketingPathForLanguage(download.MarketingContent),
			Language:
				download.Language || placeholders.downloadUnspecified || 'Unspecified',
		});
	});

	const today = new Date().getTime();
	// Use the date closest to today from all downloads in the group as the date for the grouped download
	groupedMap.forEach((groupedDownload) => {
		const dates = groupedDownload.downloads
			.map((download) => download.Date)
			.filter((date): date is number => date !== undefined);

		if (dates.length > 0) {
			const closestDate = dates.reduce((closest, current) => {
				const closestDiff = Math.abs(today - closest);
				const currentDiff = Math.abs(today - current);
				return currentDiff < closestDiff ? current : closest;
			}, dates[0]);

			groupedDownload.Date = closestDate;
		}
	});

	return Array.from(groupedMap.values());
}

export async function fetchData<T>(source: string) {
	const response = await fetch(source);
	if (!response.ok) {
		console.error('Error loading API response', response);
		return [];
	}

	const json = (await response.json()) as { data: T[] };
	if (!json) {
		console.error('Empty API response', source);
		return [];
	}

	return json.data;
}

function getLanguageOrder(language: string, currentLanguage: string) {
	const currentLangBypassCheck = (language: string, order: number) => {
		return currentLanguage === language ? -1 : order;
	};

	switch (language.toLowerCase()) {
		case 'en':
		case 'english':
			return currentLangBypassCheck('en', 0);
		case 'ja':
		case 'japanese':
			return currentLangBypassCheck('ja', 1);
		case 'fr':
		case 'french':
			return currentLangBypassCheck('fr', 2);
		case 'zh':
		case 'chinese':
			return currentLangBypassCheck('zh', 3);
		case 'de':
		case 'german':
			return currentLangBypassCheck('de', 4);
		case 'es':
		case 'spanish':
			return currentLangBypassCheck('es', 5);
		case 'it':
		case 'italian':
			return currentLangBypassCheck('it', 6);
		case 'ko':
		case 'korean':
			return currentLangBypassCheck('ko', 7);
		default:
			return Infinity;
	}
}

function sortDownloadLanguages(groupedDownloads: GroupedDownload[]) {
	const currentLanguage = getLanguageFromPath() || DEFAULT_LANG_CODE;
	groupedDownloads.forEach((groupedDownload) => {
		if (groupedDownload.downloads.length > 1) {
			groupedDownload.downloads = groupedDownload.downloads.sort((a, b) => {
				return (
					getLanguageOrder(a.Language, currentLanguage) -
					getLanguageOrder(b.Language, currentLanguage)
				);
			});
		}
	});
}

export function filterAndSortDownloads(
	term: string,
	downloads: GroupedDownload[],
	filters: FilterValues[],
	sortOptions: DownloadSortOptions,
) {
	let filteredDownloads: GroupedDownload[] = downloads;

	filters.forEach((filter) => {
		if (filter.values.length === 0) return;
		const lowerCaseValues = filter.values.map((value) => value.toLowerCase());

		if (filter.filter === 'product') {
			filteredDownloads = filteredDownloads.filter((download) => {
				return lowerCaseValues.find((value) =>
					download.Products.includes(value),
				);
			});
		}
		if (filter.filter === 'type') {
			filteredDownloads = filteredDownloads.filter((download) => {
				return (
					download.Type && lowerCaseValues.includes(download.Type.toLowerCase())
				);
			});
		}
		if (filter.filter === 'language')
			filteredDownloads = filteredDownloads.filter((download) => {
				return download.downloads.some(
					(download) =>
						download.Language &&
						lowerCaseValues.includes(download.Language.toLowerCase()),
				);
			});
	});

	const lowercaseTerm = term.toLowerCase();
	filteredDownloads = filteredDownloads.filter((download) => {
		const productIndex = download.Product.toLowerCase().indexOf(lowercaseTerm);
		const titleIndex =
			download.Title?.toLowerCase().indexOf(lowercaseTerm) ?? -1;
		const versionIndex =
			download.Version?.toLowerCase().indexOf(lowercaseTerm) ?? -1;

		return Math.max(...[productIndex, titleIndex, versionIndex]) > -1;
	});

	sortDownloadLanguages(filteredDownloads);

	if (!sortOptions.column) {
		return filteredDownloads;
	}

	const direction = sortOptions.direction || 'desc';

	return filteredDownloads.sort((a, b) => {
		if (sortOptions.column === 'date') {
			const blankFallback = direction === 'desc' ? 0 : Number.MAX_SAFE_INTEGER;
			const aDate = a.Date || blankFallback;
			const bDate = b.Date || blankFallback;
			return direction === 'asc' ? aDate - bDate : bDate - aDate;
		} else {
			if (direction === 'asc' && !a.Version) return 1;
			if (direction === 'desc' && !b.Version) return 1;

			const aVersion = a.Version || '';
			const bVersion = b.Version || '';
			return direction === 'asc'
				? aVersion.localeCompare(bVersion)
				: bVersion.localeCompare(aVersion);
		}
	});
}

function buildProductFilterValues(
	products: DownloadProduct[],
): NestedFilterValue[] {
	return products.reduce<NestedFilterValue[]>((acc, product) => {
		// Find existing tier1 node or create new one
		let tier1Node = acc.find((n) => n.value === product.Tier1);
		if (!tier1Node) {
			tier1Node = {
				value: product.Tier1,
				label: product.Tier1,
				children: [],
			};
			acc.push(tier1Node);
		}

		if (!product.Tier2) return acc;

		// Find existing tier2 node or create new one
		let tier2Node = tier1Node.children?.find((n) => n.value === product.Tier2);
		if (!tier2Node) {
			tier2Node = {
				value: product.Tier2,
				label: product.Tier2,
				children: [],
			};
			tier1Node.children!.push(tier2Node);
		}

		if (!product.Tier3) return acc;

		// Find existing tier3 node or create new one
		let tier3Node = tier2Node?.children?.find((n) => n.value === product.Tier3);
		if (!tier3Node) {
			tier3Node = {
				value: product.Tier3,
				label: product.Tier3,
				children: [],
			};
			tier2Node.children!.push(tier3Node);
		}

		// Add tier4 as leaf node if it exists
		if (product.Tier4) {
			tier3Node.children!.push({
				value: product.Tier4,
				label: product.Tier4,
				children: [],
			});
		}

		return acc;
	}, []);
}

export function createSearchFilters(
	products: DownloadProduct[],
	types: DownloadType[],
	languages: DownloadLanguage[],
	placeholders: Record<string, string>,
	onUpdated: (filters: FilterValues[]) => void,
) {
	const productFilter = {
		filter: 'product',
		filterLabel: placeholders.downloadsProduct || 'Product',
		data: {} as DropdownWidgetData,
		nestedValues:
			products.length > 0 && products[0].Tier1
				? buildProductFilterValues(products)
				: [],
	} satisfies FilterDefinition;

	if (products.length > 0 && !products[0].Tier1) {
		const legacyProducts = products as unknown as DownloadProductLegacy[];
		if (legacyProducts[0].Product) {
			legacyProducts.forEach((product) => {
				productFilter.data[product.Product] = {
					defaultLabel: product.Product,
					id: `product-${product.Product}`,
					value: product.Product,
				};
			});
		}
	}

	const typeFilter = {
		filter: 'type',
		filterLabel: placeholders.downloadsType || 'Type',
		data: {} as DropdownWidgetData,
	} satisfies FilterDefinition;

	types.forEach((type) => {
		typeFilter.data[type.Type] = {
			defaultLabel: type.Type,
			id: `type-${type.Type}`,
			value: type.Type,
		};
	});

	const languageFilter = {
		filter: 'language',
		filterLabel: placeholders.downloadsLanguage || 'Language',
		data: {} as DropdownWidgetData,
	} satisfies FilterDefinition;

	languages.forEach((language) => {
		languageFilter.data[language.Language] = {
			defaultLabel: language.Language,
			id: `language-${language.Language}`,
			value: language.Language,
		};
	});

	const unspecifiedLanguage = placeholders.downloadUnspecified || 'Unspecified';
	if (!languageFilter.data[unspecifiedLanguage]) {
		languageFilter.data[unspecifiedLanguage] = {
			defaultLabel: unspecifiedLanguage,
			id: `language-${unspecifiedLanguage}`,
			value: unspecifiedLanguage,
		};
	}

	return new SearchFilters({
		placeholders,
		filters: [productFilter, typeFilter, languageFilter],
		onUpdated,
	});
}
